/**
 * **매장 선택 창의 문구 한 곳** — 의존성이 없는 순수 모듈이다(Vue·Element·`@/` 별칭 금지).
 *
 * 서버(`/scope/tree/**`)는 어휘(`custTp`·`useYn`)만 내리고 한글 문장은 프론트가 만든다 —
 * `callpopText.js`·`deliveryMenuNotes.js` 와 같은 결이다. 창 하나를 열두 자리가 함께 쓰므로
 * (매장 셀렉트를 쓰던 화면 전부) 문장을 컴포넌트에 다시 타이핑하지 않는다.
 *
 * 판정·변환은 [scopeTree.js](./scopeTree.js) 가 갖는다 — 이 파일에는 값이 없다.
 *
 * 계약 정본: `ordern-workspace/docs/kt-callpop/briefs/B7b-후속2-사업장종류-샵인샵그룹-brief.md` §1-5·§1-6.
 */

// 확장자를 적는 이유: 단위 시험이 이 파일을 node 에서 그대로 읽는다(node ESM 은 확장자를 추론하지 않는다)
import { STORE_SEARCH_PLACEHOLDER } from './storeSearchText.js'

export const STORE_PICKER_TEXT = Object.freeze({
  // ── 필드(창을 여는 자리) ────────────────────────────────────────────────
  /** 아직 고르지 않았을 때 칸에 보이는 말 */
  placeholder: '매장을 고르세요',
  /** 고를 수 있는 매장이 하나뿐이면 창을 열지 않는다 — 그 사실을 칸이 말한다 */
  onlyStoreHint: '이 계정이 관리하는 매장입니다.',
  /**
   * 고른 매장의 정보를 읽지 못했을 때 — **선택이 되돌려졌다는 사실과 사람이 할 일**을 함께 말한다.
   *
   * 범위 밖(404)과 서버 오류를 **갈라 말하지 않는다**: 갈라 말하면 「이 코드는 있고 저 코드는 없다」가
   * 되어 매장 코드 열거의 신호가 된다. 어느 쪽이든 그 화면이 할 일은 하나다 — 다시 고르는 것.
   */
  optionLoadFailed: '그 매장의 정보를 읽지 못했습니다. 매장을 다시 고르세요.',
  /**
   * 같은 실패라도 **폼을 채우던 중**이면 말이 다르다 — 사람이 방금 고른 것이 아니므로 되돌릴 선택이
   * 없고(값은 그대로 둔다), 수정 폼에는 다시 고를 칸도 없다. 안 선 것은 언어 탭뿐이라 그것만 말한다.
   */
  optionSeedFailed: '이 매장의 언어 설정을 읽지 못했습니다. 다국어 입력이 보이지 않을 수 있습니다.',
  /**
   * **주소(`?storeCd=`)가 정한 화면의 매장**을 읽지 못했을 때 — 선택기는 비워졌고(되돌릴 확정 매장이 없다) 사람은 고른
   * 적이 없다. 그래서 「다시」가 아니라 사실과 할 일을 말한다. **서버가 그 매장을 확실히 주지 않았을 때만**(빈 배열·400·
   * 403·404) 쓴다 — 그 갈래끼리는 갈라 말하지 않고(매장 코드 열거의 신호) 이유는 「~일 수 있다」로만 적는다. 그 밖의 실패
   * (일시적 4xx·401·5xx·전송 실패·코드 오류)는 이 문장이 틀리므로 일반 오류 안내다(리뷰 L2·N2).
   *
   * 할인 화면의 `?storeCd=` 와 매장 관리의 `?storeCd=`(대행 진입·북마크, R-K2 J4)가 같은 판정(`storeSaveTarget` 의
   * `storeReadFailureNotice`)으로 이 문장을 쓴다.
   */
  optionRouteFailed: '주소의 매장을 열 수 없습니다. 없는 매장이거나 관리 범위 밖일 수 있습니다. 매장을 고르세요.',

  // ── 창 ──────────────────────────────────────────────────────────────────
  dialogTitle: '매장 선택',
  /** 서버가 한 칸으로 찾는 축 그대로(매장 목록·요금·결제와 같은 말 + 이 창만 찾는 사업장명) */
  searchPlaceholder: `${STORE_SEARCH_PLACEHOLDER} · 사업장명`,
  /** 두 자 미만은 서버가 400 이다(§1-5) — 보내기 전에 화면이 먼저 말한다 */
  searchTooShort: '두 글자 이상 입력하세요.',
  searchEmpty: '찾는 매장이 없습니다.',
  searching: '찾는 중…',
  treeEmpty: '볼 수 있는 매장이 없습니다.',
  loadFailed: '목록을 불러오지 못했습니다.',
  retryAction: '다시 시도',
  moreAction: '더 보기',
  closeAction: '닫기',
  cancelAction: '취소',

  /** 최근 고른 매장 — 계정마다 따로, 그 탭이 닫히면 사라진다 */
  recentTitle: '최근 고른 매장',
  /** 검색 결과·최근 목록의 경로 줄(뿌리 › … › 부모) 앞에 붙는 말 */
  pathLabel: '소속',

  // ── 어휘 → 한글 (판정은 scopeTree.js) ───────────────────────────────────
  /** 사업장 종류 배지 — `custTp:'SHOP_GROUP'` 하나뿐이고 그 밖은 배지가 없다(§1-1) */
  shopGroupBadge: '샵인샵 그룹',
  inactiveBadge: '비활성',
  /** 사업장 노드의 매장 수 — 「직접 3 · 전체 12」 */
  storeCountLabel: '매장',
  subtreeCountLabel: '전체',
  storeCountUnit: '곳',

  /** 키보드 안내 — 창 아래 한 줄 */
  /** 단일 모드 — 트리를 펴는 길은 `→ ←` 뿐이다(Enter 는 매장을 고른다). 안내하지 않으면 없는 길이다 */
  keyboardHint: '↑↓ 이동 · → ← 펼침·접힘 · Enter 선택 · Esc 닫기',

  // ── 다중 선택(통계 화면) ────────────────────────────────────────────────
  /**
   * 「직접 소속 매장」 묶음 행 — **「하나만 고르기」를 보이게 만든 줄**이다(§1-6).
   * 접은 채 체크하면 하부 전체, 이 줄을 체크하면 그 사업장에 직접 달린 매장만.
   */
  directGroupLabel: '직접 소속 매장',
  confirmAction: '확정',
  clearAction: '전체 해제',
  /** 칩의 꼬리표 — 「피버트 전체 737」·「바로고 동해 직접 14」 */
  chipAll: '전체',
  chipDirect: '직접',
  summaryEmpty: '아직 고른 매장이 없습니다.',
  summaryPrefix: '선택',
  /** 수를 모르는 규칙이 섞였을 때 — 확정하면 서버가 센다(지어내지 않는다) */
  summaryUnknown: '선택한 규칙의 매장 수는 확정할 때 셉니다.',
  /** 서버 상한(한 목록 1,000·합계 3,000)을 넘겼을 때 — 400 을 받기 전에도, 받은 뒤에도 같은 말이다 */
  tooManyRules: '선택이 너무 많습니다. 범위를 줄여 주세요.',
  /**
   * 다중 모드 — Enter 의 예외를 **둘 다** 적는다. `activateRow` 가 Enter 를 가져가는 줄이
   * 「더 보기」와 검색 결과의 사업장 둘이라, 하나만 적으면 바닥글이 반만 참이다.
   */
  keyboardHintMultiple:
    '↑↓ 이동 · → ← 펼침·접힘 · Space 선택 · Enter 확정(「더 보기」는 다음 장 · 검색 결과의 사업장은 들어가기) · Esc 닫기',
  /** 다중 모드 트리거 칸 — 「피버트 전체 외 1건 · 516곳」 */
  summaryMore: '외',
  summaryMoreUnit: '건',
})
